import { FC, useState } from 'react';

import { IPolicySettingsField } from '@entities/policy';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, Typography } from '@mui/material';
import { ISelectMultiOption, SelectMulti } from '@shared/components/Form';

import {
  DIGIT_OPERATORS,
  DigitOperator,
  DigitParam,
  MultiParam,
  RuleParam,
  TagDigitParam,
} from './policyRuleTypes';

interface IProps {
  title: string;
  items: RuleParam[];
  available: IPolicySettingsField[];
  onAdd: (opt: IPolicySettingsField) => void;
  onRemove: (techname: string) => void;
  onUpdate: (items: RuleParam[]) => void;
}

const PolicyRuleParamsSection: FC<IProps> = ({
  title,
  items,
  available,
  onAdd,
  onRemove,
  onUpdate,
}) => {
  const isZeroAllowedCountParam = (param: { label: string; techname: string }) => {
    const normalizedLabel = param.label.trim().toLowerCase();
    const normalizedTechname = param.techname.trim().toLowerCase();

    return (
      normalizedLabel === 'active wallets count' ||
      normalizedLabel === 'active boi wallets count' ||
      normalizedLabel === 'active wallets in tag count' ||
      normalizedLabel === 'active wallets not in tag count' ||
      normalizedTechname === 'active_wallets_count' ||
      normalizedTechname === 'active_boi_wallets_count' ||
      normalizedTechname === 'active_wallets_in_tag_count' ||
      normalizedTechname === 'active_wallets_not_in_tag_count'
    );
  };

  const resolveMin = (param: DigitParam | TagDigitParam) => {
    const isCountParam =
      param.label.toLowerCase().includes('count') || param.techname.toLowerCase().includes('count');
    if (!isCountParam) return undefined;
    return isZeroAllowedCountParam(param) ? 0 : 1;
  };

  const updateItem = (techname: string, patch: Partial<TagDigitParam>) => {
    onUpdate(
      items.map(item => (item.techname === techname ? ({ ...item, ...patch } as RuleParam) : item)),
    );
  };

  const [selectedTechname, setSelectedTechname] = useState('');
  const selectedOption = available.find(opt => opt.techname === selectedTechname);
  const emptyMessage = title.toLowerCase().includes('condition')
    ? 'No condition parameters added. Click "Add Param" to specify conditions.'
    : 'No selection parameters added. Click "Add Param" to specify targeting.';

  const renderTagsSelect = (param: MultiParam | TagDigitParam) => (
    <SelectMulti
      name={param.techname}
      options={Object.entries(param.values).map(([value, label]) => ({
        value,
        label,
      }))}
      value={param.selected}
      onChange={val => updateItem(param.techname, { selected: val as ISelectMultiOption[] })}
    />
  );

  const renderDigitControls = (param: DigitParam | TagDigitParam) => (
    <Stack direction='row' spacing={1} alignItems='center'>
      <select
        aria-label={`${param.label} operator`}
        value={param.operator}
        onChange={e => updateItem(param.techname, { operator: e.target.value as DigitOperator })}
      >
        {DIGIT_OPERATORS.map(op => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
      <input
        type='number'
        aria-label={`${param.label} value`}
        min={resolveMin(param)}
        value={param.value}
        onChange={e => updateItem(param.techname, { value: e.target.value })}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Typography variant='subtitle2' fontWeight={600}>
        {title}
      </Typography>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ width: '100%' }}>
          <select value={selectedTechname} onChange={e => setSelectedTechname(e.target.value)}>
            <option value=''>Add Param</option>
            {available.map(opt => (
              <option key={opt.techname} value={opt.techname}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type='button'
            className='secondary-btn policy-rule-params-add-btn'
            onClick={() => {
              /* istanbul ignore else -- defensive: the button is disabled unless an option is selected */
              if (selectedOption) onAdd(selectedOption);
              setSelectedTechname('');
            }}
            disabled={!selectedOption}
          >
            Add Param
          </button>
        </Stack>
      </Stack>

      {items.length === 0 && (
        <Typography variant='caption' color='text.secondary' textAlign='center'>
          {emptyMessage}
        </Typography>
      )}

      {items.map(param => (
        <Stack
          key={param.techname}
          spacing={1}
          sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='caption'>{param.label}</Typography>
            <IconButton size='small' color='error' onClick={() => onRemove(param.techname)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Stack>

          {param.type === 'multi-select' && renderTagsSelect(param)}
          {param.type === 'digit-compare' && renderDigitControls(param)}
          {param.type === 'tag-digit-compare' && (
            <Stack spacing={1}>
              {renderTagsSelect(param)}
              {renderDigitControls(param)}
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default PolicyRuleParamsSection;
